import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
  Tabs,
  Tab,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import OperationProgress from '../components/OperationProgress';
import { useCopyProgress } from '../hooks/useCopyProgress';
import { handleTitleCaseChange } from '../utils/textUtils';
import { createTooltipProps } from '../utils/fieldTooltips';
import { ValidationRules, validateField } from '../utils/validation';
import {
  appendCategoryNameBulkUpdateFlag,
  categoryNameUpdateSuccessMessage,
  maybeConfirmCategoryNameBulkUpdate,
} from '../utils/categoryNameBulkUpdate';
import {
  HIRE_STOCK_FINANCIAL_FIELDS,
  HIRE_STOCK_PROP_FIELDS,
  HIRE_STOCK_PROVISIONAL_TYPES,
  isKnownProvisionalType,
  isValueInOptions,
  normalizeHireStockFormData,
  createEmptyHireStockForm,
  buildHireStockSavePayload,
} from '../constants/hireStockIndividual';
import ApplyRateModal from '../components/ApplyRateModal';
import MissingSelectOption from '../components/MissingSelectOption';

function HireStockModal({ hireStockId, onClose, onSave, defaultCategory = '' }) {
  const [formData, setFormData] = useState(createEmptyHireStockForm(defaultCategory));
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [hireCategories, setHireCategories] = useState([]);
  const [depots, setDepots] = useState([]);
  const [salesNominals, setSalesNominals] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [originalName, setOriginalName] = useState('');
  const [originalCategory, setOriginalCategory] = useState('');
  const [loadedSnapshot, setLoadedSnapshot] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadRecord = async () => {
      if (!hireStockId) {
        setFormData(createEmptyHireStockForm(defaultCategory));
        setOriginalName('');
        setOriginalCategory(defaultCategory || '');
        setLoadedSnapshot(null);
        setLoadError('');
        setLoadingRecord(false);
        return;
      }

      setLoadingRecord(true);
      setLoadError('');
      try {
        const response = await axios.get(`/api/hire-stock/${hireStockId}`);
        if (!cancelled) {
          const normalized = normalizeHireStockFormData(response.data);
          setFormData(normalized);
          setOriginalName(normalized.name || '');
          setOriginalCategory(normalized.cat || '');
          setLoadedSnapshot(normalized);
        }
      } catch (error) {
        console.error('Error loading hire stock item:', error);
        if (!cancelled) {
          setLoadError('Failed to load hire stock item');
          setFormData(createEmptyHireStockForm(defaultCategory));
        }
      } finally {
        if (!cancelled) {
          setLoadingRecord(false);
        }
      }
    };

    loadRecord();
    fetchHireCategories();
    fetchDepots();
    fetchNominalCodes();

    return () => {
      cancelled = true;
    };
  }, [hireStockId, defaultCategory]);

  const fetchHireCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/hire-categories?limit=1000', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHireCategories(response.data.hireCategories || []);
    } catch (error) {
      console.error('Error fetching hire categories:', error);
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await axios.get('/api/depots?limit=1000');
      setDepots(response.data.depots || []);
    } catch (error) {
      console.error('Error fetching depots:', error);
    }
  };

  const fetchNominalCodes = async () => {
    try {
      const response = await axios.get('/api/nominal-codes', {
        params: { type: 'S', limit: 500 },
      });
      setSalesNominals(response.data.nominalCodes || []);
    } catch (error) {
      console.error('Error fetching nominal codes:', error);
    }
  };

  const titleCaseFields = ['name', 'supplier', 'make', 'model'];

  const handleChange = (e) => {
    handleTitleCaseChange(e, setFormData, formData, titleCaseFields);
  };

  const isUnchangedLoadedValue = (field) =>
    loadedSnapshot != null && formData[field] === loadedSnapshot[field];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate financial fields
    const errors = {};
    [
      ...HIRE_STOCK_FINANCIAL_FIELDS,
      'weight',
      'switch_after_weeks',
      'hp_over_months',
      'last_meter_rdg',
    ].forEach((field) => {
      if (formData[field] !== undefined && formData[field] !== '') {
        const error = validateField(
          field,
          formData[field],
          ValidationRules.hireStock[field],
          formData
        );
        if (error) {
          errors[field] = error;
        }
      }
    });

    ['cat', 'stock_no', 'name'].forEach((field) => {
      const error = validateField(
        field,
        formData[field],
        ValidationRules.hireStock[field],
        formData
      );
      if (error) {
        errors[field] = error;
      }
    });

    if (
      formData.cat &&
      !isUnchangedLoadedValue('cat') &&
      !isValueInOptions(formData.cat, hireCategories, (category) => category.code)
    ) {
      errors.cat = `Category "${formData.cat}" is not in the lookup list`;
    }

    if (
      formData.depot &&
      !isUnchangedLoadedValue('depot') &&
      !isValueInOptions(formData.depot, depots, (depot) => depot.short_code)
    ) {
      errors.depot = `Depot "${formData.depot}" is not in the lookup list`;
    }

    if (
      formData.nom_code &&
      !isUnchangedLoadedValue('nom_code') &&
      !isValueInOptions(formData.nom_code, salesNominals, (nominal) => nominal.code)
    ) {
      errors.nom_code = `Nom Code "${formData.nom_code}" is not in the Sales nominal codes list`;
    }

    if (
      formData.provisional_type &&
      !isUnchangedLoadedValue('provisional_type') &&
      !isKnownProvisionalType(formData.provisional_type)
    ) {
      errors.provisional_type = `Provisional Type "${formData.provisional_type}" is not in the lookup list`;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const errorFields = Object.keys(errors).join(', ');
      setSaveError(`Please fix the highlighted fields before saving (${errorFields}).`);
      return;
    }

    setValidationErrors({});
    setSaveError('');
    setSaving(true);
    try {
      let updateCategoryNameMatches = false;
      if (hireStockId) {
        try {
          updateCategoryNameMatches = await maybeConfirmCategoryNameBulkUpdate(axios, {
            matchUrl: '/api/hire-stock/name-matches',
            category: formData.cat || originalCategory,
            oldName: originalName,
            newName: formData.name,
            excludeId: hireStockId,
            entityLabel: 'hire stock items',
          });
        } catch (matchError) {
          console.error('Failed to check for matching hire stock names:', matchError);
        }
      }

      await onSave(buildHireStockSavePayload(formData), { updateCategoryNameMatches });
    } catch (error) {
      const message =
        error.message || error.response?.data?.error || 'Failed to save hire stock item';
      setSaveError(message);
      const lower = message.toLowerCase();
      if (lower.includes('stock number') || lower.includes('item code')) {
        setValidationErrors({ stock_no: message });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>{hireStockId ? 'Edit Hire Stock' : 'Add New Hire Stock'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {loadError && (
            <div className="alert alert-danger py-2" role="alert">
              {loadError}
            </div>
          )}
          {saveError && (
            <div className="alert alert-danger py-2" role="alert">
              {saveError}
            </div>
          )}
          {loadingRecord ? (
            <div className="text-center py-5 text-muted">Loading hire stock item...</div>
          ) : (
            <Tabs defaultActiveKey="identity" className="mb-3">
              {/* ── Tab 1: Identity ── */}
              <Tab eventKey="identity" title="Identity">
                <Container fluid>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Category</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('cat')}>
                              Used in equipment classification, pricing, and hire contract
                              management
                            </Tooltip>
                          }
                        >
                          <Form.Select
                            name="cat"
                            value={formData.cat || ''}
                            onChange={handleChange}
                            isInvalid={!!validationErrors.cat}
                            {...createTooltipProps('cat')}
                          >
                            <option value="">Select Category</option>
                            <MissingSelectOption
                              value={formData.cat}
                              options={hireCategories}
                              getOptionValue={(category) => category.code}
                            />
                            {hireCategories.map((category) => (
                              <option key={category.id} value={category.code}>
                                {category.name}
                              </option>
                            ))}
                          </Form.Select>
                        </OverlayTrigger>
                        {validationErrors.cat && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.cat}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Stock No</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('stock_no')}>
                              Used for hire contract creation, availability tracking, and equipment
                              management
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="stock_no"
                            value={formData.stock_no}
                            onChange={handleChange}
                            maxLength="20"
                            isInvalid={!!validationErrors.stock_no}
                            {...createTooltipProps('stock_no')}
                          />
                        </OverlayTrigger>
                        {validationErrors.stock_no && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.stock_no}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('name')}>
                              Used in equipment selection, contracts, and customer-facing documents
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            maxLength="120"
                            {...createTooltipProps('name')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Serial No</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('serial_no')}>
                              Used for warranty tracking, service history, and asset identification
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="serial_no"
                            value={formData.serial_no}
                            onChange={handleChange}
                            maxLength="50"
                            {...createTooltipProps('serial_no')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Supplier</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('supplier')}>
                              Equipment supplier or manufacturer
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="supplier"
                            value={formData.supplier}
                            onChange={handleChange}
                            maxLength="60"
                            {...createTooltipProps('supplier')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Depot</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('depot')}>
                              Used for availability checking, delivery planning, and equipment
                              tracking
                            </Tooltip>
                          }
                        >
                          <Form.Select
                            name="depot"
                            value={formData.depot || ''}
                            onChange={handleChange}
                            isInvalid={!!validationErrors.depot}
                            {...createTooltipProps('depot')}
                          >
                            <option value="">Select a depot...</option>
                            <MissingSelectOption
                              value={formData.depot}
                              options={depots}
                              getOptionValue={(depot) => depot.short_code}
                            />
                            {depots.map((depot) => (
                              <option key={depot.id} value={depot.short_code}>
                                {depot.depot_name}
                              </option>
                            ))}
                          </Form.Select>
                        </OverlayTrigger>
                        {validationErrors.depot && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.depot}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Last Meter Reading</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('last_meter_rdg')}>
                              Last recorded meter reading for equipment usage tracking
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="last_meter_rdg"
                            value={formData.last_meter_rdg}
                            onChange={handleChange}
                            {...createTooltipProps('last_meter_rdg')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex align-items-center">
                      <Form.Group className="mb-3 mt-2">
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('active')}>
                              Controls whether equipment is available for hire
                            </Tooltip>
                          }
                        >
                          <Form.Check
                            type="checkbox"
                            name="active"
                            label="Active"
                            checked={formData.active}
                            onChange={handleChange}
                            {...createTooltipProps('active')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>
                </Container>
              </Tab>

              {/* ── Tab 2: Pricing ── */}
              <Tab eventKey="pricing" title="Pricing">
                <Container fluid>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Week Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('week_rate')}>
                              Used for longer-term hire contracts and pricing options
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="week_rate"
                            value={formData.week_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            isInvalid={!!validationErrors.week_rate}
                            {...createTooltipProps('week_rate')}
                          />
                        </OverlayTrigger>
                        {validationErrors.week_rate && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.week_rate}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>1 Day Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('one_day_rate')}>
                              Used in hire contract pricing and revenue calculations
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="one_day_rate"
                            value={formData.one_day_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            isInvalid={!!validationErrors.one_day_rate}
                            {...createTooltipProps('one_day_rate')}
                          />
                        </OverlayTrigger>
                        {validationErrors.one_day_rate && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.one_day_rate}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>2 Days Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('two_day_rate')}>
                              Two-day hire rate
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="two_day_rate"
                            value={formData.two_day_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('two_day_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>3 Days Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('three_day_rate')}>
                              Three-day hire rate
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="three_day_rate"
                            value={formData.three_day_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('three_day_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>4 Days Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('four_day_rate')}>
                              Four-day hire rate
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="four_day_rate"
                            value={formData.four_day_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('four_day_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Day Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('day_rate')}>
                              Standard daily hire rate for the equipment
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="day_rate"
                            value={formData.day_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            isInvalid={!!validationErrors.day_rate}
                            {...createTooltipProps('day_rate')}
                          />
                        </OverlayTrigger>
                        {validationErrors.day_rate && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.day_rate}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Month Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('month_rate')}>
                              Used for monthly hire contracts and pricing options
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="month_rate"
                            value={formData.month_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            isInvalid={!!validationErrors.month_rate}
                            {...createTooltipProps('month_rate')}
                          />
                        </OverlayTrigger>
                        {validationErrors.month_rate && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.month_rate}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Part Day Rate</Form.Label>
                        <Form.Control
                          type="number"
                          name="part_day_rate"
                          value={formData.part_day_rate || ''}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('part_day_rate')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>W/E Rate</Form.Label>
                        <Form.Control
                          type="number"
                          name="weekend_rate"
                          value={formData.weekend_rate || ''}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('weekend_rate')}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Hour Rate</Form.Label>
                        <Form.Control
                          type="number"
                          name="hour_rate"
                          value={formData.hour_rate || ''}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('hour_rate')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>SC Rate</Form.Label>
                        <Form.Control
                          type="number"
                          name="sc_rate"
                          value={formData.sc_rate || ''}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('sc_rate')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Unit Rate</Form.Label>
                        <Form.Control
                          type="number"
                          name="unit_rate"
                          value={formData.unit_rate || ''}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('unit_rate')}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Switch Week Rate</Form.Label>
                        <Form.Control
                          type="number"
                          name="switch_week_rate"
                          value={formData.switch_week_rate || ''}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('switch_week_rate')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Switch After Weeks</Form.Label>
                        <Form.Control
                          type="number"
                          name="switch_after_weeks"
                          value={formData.switch_after_weeks || ''}
                          onChange={handleChange}
                          min="1"
                          {...createTooltipProps('switch_after_weeks')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Deposit</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('deposit')}>
                              Security deposit required for equipment hire
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="deposit"
                            value={formData.deposit || ''}
                            onChange={handleChange}
                            step="0.01"
                            isInvalid={!!validationErrors.deposit}
                            {...createTooltipProps('deposit')}
                          />
                        </OverlayTrigger>
                        {validationErrors.deposit && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.deposit}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </Container>
              </Tab>

              {/* ── Tab 3: Purchase & Sale ── */}
              <Tab eventKey="purchase" title="Purchase & Sale">
                <Container fluid>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Purchase Date</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('purch_date')}>
                              Used for depreciation calculations, warranty tracking, and age
                              analysis
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="date"
                            name="purch_date"
                            value={formData.purch_date || ''}
                            onChange={handleChange}
                            isInvalid={!!validationErrors.purch_date}
                            {...createTooltipProps('purch_date')}
                          />
                        </OverlayTrigger>
                        {validationErrors.purch_date && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.purch_date}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Purchase Price</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('purch_price')}>
                              Used for depreciation calculations, insurance, and asset valuation
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="purch_price"
                            value={formData.purch_price || ''}
                            onChange={handleChange}
                            step="0.01"
                            isInvalid={!!validationErrors.purch_price}
                            {...createTooltipProps('purch_price')}
                          />
                        </OverlayTrigger>
                        {validationErrors.purch_price && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.purch_price}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Sale Date</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('sale_date')}>
                              Date when the equipment was sold
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="date"
                            name="sale_date"
                            value={formData.sale_date || ''}
                            onChange={handleChange}
                            {...createTooltipProps('sale_date')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Sale Value</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('sale_value')}>
                              Value received when equipment was sold
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="sale_value"
                            value={formData.sale_value || ''}
                            onChange={handleChange}
                            step="0.01"
                            isInvalid={!!validationErrors.sale_value}
                            {...createTooltipProps('sale_value')}
                          />
                        </OverlayTrigger>
                        {validationErrors.sale_value && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.sale_value}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Sale Invoice</Form.Label>
                        <Form.Control
                          type="text"
                          name="sale_invoice"
                          value={formData.sale_invoice}
                          onChange={handleChange}
                          maxLength="20"
                          {...createTooltipProps('sale_invoice')}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Provisional Type</Form.Label>
                        <Form.Select
                          name="provisional_type"
                          value={formData.provisional_type || ''}
                          onChange={handleChange}
                          isInvalid={!!validationErrors.provisional_type}
                          {...createTooltipProps('provisional_type')}
                        >
                          <option value="">Select type...</option>
                          <MissingSelectOption
                            value={formData.provisional_type}
                            options={HIRE_STOCK_PROVISIONAL_TYPES}
                            getOptionValue={(option) => option.value}
                          />
                          {HIRE_STOCK_PROVISIONAL_TYPES.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                        {validationErrors.provisional_type && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.provisional_type}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Nom Code</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('nom_code')}>
                              Sales nominal code (N/C - Sales)
                            </Tooltip>
                          }
                        >
                          <Form.Select
                            name="nom_code"
                            value={formData.nom_code || ''}
                            onChange={handleChange}
                            isInvalid={!!validationErrors.nom_code}
                            {...createTooltipProps('nom_code')}
                          >
                            <option value="">Select nominal...</option>
                            <MissingSelectOption
                              value={formData.nom_code}
                              options={salesNominals}
                              getOptionValue={(nominal) => nominal.code}
                            />
                            {salesNominals.map((nc) => (
                              <option key={nc.id} value={nc.code}>
                                {nc.code} - {nc.name}
                              </option>
                            ))}
                          </Form.Select>
                        </OverlayTrigger>
                        {validationErrors.nom_code && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {validationErrors.nom_code}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Depreciation Rate</Form.Label>
                        <Form.Control
                          type="text"
                          name="depreciation_rate"
                          value={formData.depreciation_rate}
                          onChange={handleChange}
                          maxLength="5"
                          {...createTooltipProps('depreciation_rate')}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Insurance Value</Form.Label>
                        <Form.Control
                          type="number"
                          name="insurance_value"
                          value={formData.insurance_value || ''}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('insurance_value')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Previous Income</Form.Label>
                        <Form.Control
                          type="number"
                          name="previous_income"
                          value={formData.previous_income || ''}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('previous_income')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Previous Cost Parts</Form.Label>
                        <Form.Control
                          type="number"
                          name="previous_cost_parts"
                          value={formData.previous_cost_parts || ''}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('previous_cost_parts')}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Previous Cost Labour</Form.Label>
                        <Form.Control
                          type="number"
                          name="previous_cost_labour"
                          value={formData.previous_cost_labour || ''}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('previous_cost_labour')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Fixed Monthly Cost</Form.Label>
                        <Form.Control
                          type="number"
                          name="fixed_monthly_cost"
                          value={formData.fixed_monthly_cost || ''}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('fixed_monthly_cost')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Anticipated Weekly Income</Form.Label>
                        <Form.Control
                          type="number"
                          name="anticipated_weekly_income"
                          value={formData.anticipated_weekly_income || ''}
                          onChange={handleChange}
                          step="0.01"
                          {...createTooltipProps('anticipated_weekly_income')}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Container>
              </Tab>

              <Tab eventKey="warranty" title="Warranty & Inspection">
                <Container fluid>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Inspection Test</Form.Label>
                        <Form.Control
                          type="text"
                          name="inspection_test"
                          value={formData.inspection_test}
                          onChange={handleChange}
                          maxLength="60"
                          {...createTooltipProps('inspection_test')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Warranty Type</Form.Label>
                        <Form.Control
                          type="text"
                          name="warranty_type"
                          value={formData.warranty_type}
                          onChange={handleChange}
                          maxLength="60"
                          {...createTooltipProps('warranty_type')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Warranty Supplier</Form.Label>
                        <Form.Control
                          type="text"
                          name="warranty_supplier"
                          value={formData.warranty_supplier}
                          onChange={handleChange}
                          {...createTooltipProps('warranty_supplier')}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Warranty Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="warranty_start_date"
                          value={formData.warranty_start_date || ''}
                          onChange={handleChange}
                          {...createTooltipProps('warranty_start_date')}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Container>
              </Tab>

              <Tab eventKey="hp" title="HP">
                <Container fluid>
                  <Row>
                    <Col md={4} className="d-flex align-items-center">
                      <Form.Group className="mb-3 mt-2">
                        <Form.Check
                          type="checkbox"
                          name="on_hp"
                          label="On HP?"
                          checked={!!formData.on_hp}
                          onChange={(e) => setFormData({ ...formData, on_hp: e.target.checked })}
                          {...createTooltipProps('on_hp')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>HP Notes</Form.Label>
                        <Form.Control
                          type="text"
                          name="hp_notes"
                          value={formData.hp_notes}
                          onChange={handleChange}
                          maxLength="30"
                          {...createTooltipProps('hp_notes')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>(HP) Over Months</Form.Label>
                        <Form.Control
                          type="number"
                          name="hp_over_months"
                          value={formData.hp_over_months || ''}
                          onChange={handleChange}
                          min="1"
                          {...createTooltipProps('hp_over_months')}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Container>
              </Tab>

              <Tab eventKey="tracking" title="Tracking">
                <Container fluid>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Barcode</Form.Label>
                        <Form.Control
                          type="text"
                          name="barcode"
                          value={formData.barcode}
                          onChange={handleChange}
                          maxLength="20"
                          {...createTooltipProps('barcode')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>RFID</Form.Label>
                        <Form.Control
                          type="text"
                          name="rfid"
                          value={formData.rfid}
                          onChange={handleChange}
                          maxLength="20"
                          {...createTooltipProps('rfid')}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>QR Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="qr_code"
                          value={formData.qr_code}
                          onChange={handleChange}
                          maxLength="50"
                          {...createTooltipProps('qr_code')}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Alert Text</Form.Label>
                        <Form.Control
                          as="textarea"
                          name="alert_text"
                          value={formData.alert_text}
                          onChange={handleChange}
                          maxLength="512"
                          rows="3"
                          {...createTooltipProps('alert_text')}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Container>
              </Tab>

              {/* ── Tab 4: Spec & Notes ── */}
              <Tab eventKey="spec" title="Spec & Notes">
                <Container fluid>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reg Number</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('reg_number')}>
                              Registration number for vehicles and registered equipment
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="reg_number"
                            value={formData.reg_number || ''}
                            onChange={handleChange}
                            maxLength="30"
                            {...createTooltipProps('reg_number')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Make</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('make')}>
                              Used for parts sourcing, maintenance procedures, and equipment
                              categorization
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="make"
                            value={formData.make}
                            onChange={handleChange}
                            maxLength="30"
                            {...createTooltipProps('make')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Model</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('model')}>
                              Used for detailed specifications, parts sourcing, and maintenance
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="model"
                            value={formData.model}
                            onChange={handleChange}
                            maxLength="128"
                            {...createTooltipProps('model')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Weight</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('weight')}>
                              Equipment weight for transport and handling purposes
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="weight"
                            value={formData.weight || ''}
                            onChange={handleChange}
                            step="0.01"
                            min="0.01"
                            {...createTooltipProps('weight')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Additional Info</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('additional_info')}>
                              Stores additional equipment specifications, features, and custom data
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            as="textarea"
                            name="additional_info"
                            value={formData.additional_info}
                            onChange={handleChange}
                            maxLength="512"
                            rows="4"
                            {...createTooltipProps('additional_info')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Hire Notes</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('hire_notes')}>
                              Additional notes specific to hire operations
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            as="textarea"
                            name="hire_notes"
                            value={formData.hire_notes || ''}
                            onChange={handleChange}
                            maxLength="512"
                            rows="4"
                            {...createTooltipProps('hire_notes')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>
                </Container>
              </Tab>

              <Tab eventKey="properties" title="Properties">
                <Container fluid>
                  {Array.from({ length: 10 }, (_, rowIndex) => (
                    <Row key={rowIndex}>
                      {HIRE_STOCK_PROP_FIELDS.slice(rowIndex * 5, rowIndex * 5 + 5).map(
                        (field, index) => {
                          const propNumber = rowIndex * 5 + index + 1;
                          return (
                            <Col md={2} key={field}>
                              <Form.Group className="mb-3">
                                <Form.Label>{`Prop${propNumber}`}</Form.Label>
                                <Form.Control
                                  type="text"
                                  name={field}
                                  value={formData[field] || ''}
                                  onChange={handleChange}
                                  maxLength="120"
                                  {...createTooltipProps(field)}
                                />
                              </Form.Group>
                            </Col>
                          );
                        }
                      )}
                    </Row>
                  ))}
                </Container>
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loadingRecord || !!loadError || saving}>
            {saving ? 'Saving...' : hireStockId ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function HireStock() {
  const [hireStock, setHireStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingHireStockId, setEditingHireStockId] = useState(null);
  const [editingHireStockItem, setEditingHireStockItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hireCategories, setHireCategories] = useState([]);
  const [depots, setDepots] = useState([]);
  const [copyingId, setCopyingId] = useState(null);
  const { copyProgress, wrapCopy } = useCopyProgress();
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyItem, setCopyItem] = useState(null);
  const [copyCount, setCopyCount] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showApplyRateModal, setShowApplyRateModal] = useState(false);
  const [applyRateItems, setApplyRateItems] = useState([]);
  const [editingRate, setEditingRate] = useState(null); // { itemId, field }
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const fetchHireStock = async (page = 1, search = '', category = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/hire-stock', {
        params: {
          page,
          limit: pagination.limit,
          search,
          category,
        },
      });
      setHireStock(response.data.hireStock);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch hire stock items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHireStock(1, '', selectedCategory);
    fetchHireCategories();
    fetchDepots();
  }, []);

  const fetchHireCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/hire-categories?limit=1000', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHireCategories(response.data.hireCategories || []);
    } catch (error) {
      console.error('Error fetching hire categories:', error);
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await axios.get('/api/depots?limit=1000');
      setDepots(response.data.depots || []);
    } catch (error) {
      console.error('Error fetching depots:', error);
    }
  };

  const getCategoryName = (categoryCode) => {
    const category = hireCategories.find((cat) => cat.code === categoryCode);
    return category ? category.name : categoryCode;
  };

  const handleDepotChange = async (itemId, newDepot) => {
    try {
      await axios.put(`/api/hire-stock/${itemId}`, { depot: newDepot });
      // Update the local state to reflect the change
      setHireStock((prev) =>
        prev.map((item) => (item.id === itemId ? { ...item, depot: newDepot } : item))
      );
    } catch (error) {
      console.error('Error updating depot:', error);
      setError('Failed to update depot');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchHireStock(1, searchTerm, selectedCategory);
  };

  const handlePageChange = (newPage) => {
    fetchHireStock(newPage, searchTerm, selectedCategory);
  };

  const handleCategoryFilter = (categoryCode) => {
    setSelectedCategory(categoryCode);
    fetchHireStock(1, searchTerm, categoryCode);
  };

  const handleAddHireStock = () => {
    setEditingHireStockId(null);
    setEditingHireStockItem(null);
    setShowModal(true);
  };

  const handleApplyRateClick = async () => {
    if (!selectedCategory) {
      setApplyRateItems([]);
      setShowApplyRateModal(true);
      return;
    }
    try {
      const response = await axios.get('/api/hire-stock', {
        params: { page: 1, limit: 10000, category: selectedCategory },
      });
      setApplyRateItems(response.data.hireStock || []);
      setShowApplyRateModal(true);
    } catch (err) {
      setError('Failed to load items for apply rate');
    }
  };

  const handleEditHireStock = (hireStockItem) => {
    setEditingHireStockId(hireStockItem.id);
    setEditingHireStockItem(hireStockItem);
    setShowModal(true);
  };

  const handleSaveHireStock = async (hireStockData, { updateCategoryNameMatches = false } = {}) => {
    try {
      const payload = appendCategoryNameBulkUpdateFlag(hireStockData, updateCategoryNameMatches);

      if (editingHireStockId) {
        const response = await axios.put(`/api/hire-stock/${editingHireStockId}`, payload);
        let successMessage = categoryNameUpdateSuccessMessage(
          'Hire stock item updated successfully',
          response.data.categoryNameUpdates
        );

        // Check if cascade updates occurred
        if (response.data.cascadeUpdates) {
          const cascade = response.data.cascadeUpdates;
          if (typeof cascade === 'object') {
            const { inclusions, fleetReminders } = cascade;
            const totalUpdates = (inclusions || 0) + (fleetReminders || 0);

            if (totalUpdates > 0) {
              const parts = [];
              if (inclusions > 0)
                parts.push(`${inclusions} inclusion${inclusions !== 1 ? 's' : ''}`);
              if (fleetReminders > 0)
                parts.push(`${fleetReminders} fleet reminder${fleetReminders !== 1 ? 's' : ''}`);
              successMessage += `. Also updated ${parts.join(' and ')} with the new stock number.`;
            }
          } else if (typeof cascade === 'number' && cascade > 0) {
            successMessage += `. Also updated ${cascade} related record${cascade !== 1 ? 's' : ''} with the new stock number.`;
          }
        }

        setSuccess(successMessage);
      } else {
        await axios.post('/api/hire-stock', payload);
        setSuccess('Hire stock item created successfully');
      }
      setShowModal(false);
      setEditingHireStockId(null);
      setEditingHireStockItem(null);
      fetchHireStock(pagination.page, searchTerm, selectedCategory);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to save hire stock item';
      throw new Error(message);
    }
  };

  const handleDeleteHireStock = async (id) => {
    if (window.confirm('Are you sure you want to delete this hire stock item?')) {
      try {
        await axios.delete(`/api/hire-stock/${id}`);
        setSuccess('Hire stock item deleted successfully');
        fetchHireStock(pagination.page, searchTerm, selectedCategory);
      } catch (error) {
        setError('Failed to delete hire stock item');
      }
    }
  };

  const handleCopyClick = (hireStockItem) => {
    setCopyItem(hireStockItem);
    setCopyCount(1);
    setShowCopyModal(true);
  };

  const handleCopyHireStock = async (hireStockItem, count = 1) => {
    setCopyingId(hireStockItem.id);
    setShowCopyModal(false);
    try {
      const token = localStorage.getItem('token');
      const originalStockNo = hireStockItem.stock_no;

      // Smart increment: Strip trailing number and find highest existing number
      const match = originalStockNo.match(/^(.*?)(\d+)$/);
      const baseStockNo = match ? match[1] : originalStockNo;
      const originalPadLength = match ? match[2].length : 2; // Preserve zero-padding

      const existingNumbers = hireStock
        .map((item) => {
          const itemMatch = item.stock_no.match(
            new RegExp(`^${baseStockNo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\d+)$`)
          );
          return itemMatch ? parseInt(itemMatch[1]) : 0;
        })
        .filter((num) => num > 0);
      const highestNum = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;

      let baseSerialNo = null;
      let serialPadLength = 2;
      if (hireStockItem.serial_no) {
        const serialMatch = hireStockItem.serial_no.match(/^(.*?)(\d+)$/);
        baseSerialNo = serialMatch ? serialMatch[1] : hireStockItem.serial_no;
        serialPadLength = serialMatch ? serialMatch[2].length : 2;
      }
      const existingSerialNumbers = baseSerialNo
        ? hireStock
            .filter((item) => item.serial_no)
            .map((item) => {
              const itemMatch = item.serial_no.match(
                new RegExp(`^${baseSerialNo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\d+)$`)
              );
              return itemMatch ? parseInt(itemMatch[1]) : 0;
            })
            .filter((num) => num > 0)
        : [];
      const highestSerialNum =
        existingSerialNumbers.length > 0 ? Math.max(...existingSerialNumbers) : 0;

      let totalInclusionsCopied = 0;
      let totalInclusionsFound = 0;
      let totalInclusionsFailed = 0;
      let inclusionError = null;

      await wrapCopy(
        count === 1 ? 'Copying hire stock item...' : `Copying ${count} hire stock items...`,
        count,
        async (tick) => {
          for (let i = 0; i < count; i++) {
            const copiedData = { ...hireStockItem };
            delete copiedData.id;
            delete copiedData.created_at;
            delete copiedData.updated_at;

            const newNumber = highestNum + 1 + i;
            copiedData.stock_no = `${baseStockNo}${String(newNumber).padStart(originalPadLength, '0')}`;

            if (baseSerialNo) {
              const newSerialNumber = highestSerialNum + 1 + i;
              copiedData.serial_no = `${baseSerialNo}${String(newSerialNumber).padStart(serialPadLength, '0')}`;
            }

            const newStockNo = copiedData.stock_no;

            await axios.post('/api/hire-stock', copiedData, {
              headers: { Authorization: `Bearer ${token}` },
            });

            try {
              const inclusionsResponse = await axios.get(
                `/api/inclusions/by-parent/${encodeURIComponent(originalStockNo)}`,
                { params: { parentType: 'S' }, headers: { Authorization: `Bearer ${token}` } }
              );
              const stockInclusions = inclusionsResponse.data || [];
              totalInclusionsFound += stockInclusions.length;

              for (const inclusion of stockInclusions) {
                try {
                  const newInclusion = { ...inclusion };
                  delete newInclusion.id;
                  delete newInclusion.created_at;
                  delete newInclusion.updated_at;
                  delete newInclusion.user_id;
                  newInclusion.parent_code = newStockNo;
                  await axios.post('/api/inclusions', newInclusion, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  totalInclusionsCopied++;
                } catch (singleError) {
                  console.warn(
                    'Failed to copy inclusion:',
                    inclusion.incl_cat,
                    singleError.response?.data?.error
                  );
                  totalInclusionsFailed++;
                  if (!inclusionError)
                    inclusionError = singleError.response?.data?.error || singleError.message;
                }
              }
            } catch (inclError) {
              inclusionError = inclError.response?.data?.error || inclError.message;
            }

            tick({ success: true });
          }
        }
      );

      let message =
        count === 1
          ? 'Hire stock item copied successfully'
          : `${count} copies of hire stock item created successfully`;
      if (totalInclusionsCopied > 0 && totalInclusionsFailed === 0) {
        message += ` (${totalInclusionsCopied} inclusion${totalInclusionsCopied > 1 ? 's' : ''} also copied)`;
      } else if (totalInclusionsCopied > 0 && totalInclusionsFailed > 0) {
        message += ` (${totalInclusionsCopied} inclusion${totalInclusionsCopied > 1 ? 's' : ''} copied, ${totalInclusionsFailed} skipped)`;
      } else if (totalInclusionsFound > 0 && totalInclusionsFailed > 0 && inclusionError) {
        message += ` (${totalInclusionsFailed} inclusion${totalInclusionsFailed > 1 ? 's' : ''} skipped - ${inclusionError})`;
      }
      setSuccess(message);
      fetchHireStock(pagination.page, searchTerm, selectedCategory);
    } catch (error) {
      setError('Failed to copy hire stock item');
    } finally {
      setCopyingId(null);
    }
  };

  const handleDeleteAllHireStock = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL hire stock items? This action cannot be undone!'
      )
    ) {
      if (
        window.confirm(
          'This will permanently delete all your hire stock records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/hire-stock');
          setSuccess(
            `All hire stock items deleted successfully (${response.data.deletedCount} records)`
          );
          setSelectedCategory('');
          fetchHireStock(1, '', '');
        } catch (error) {
          setError('Failed to delete all hire stock items');
        }
      }
    }
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center">
            <i className="fas fa-tools me-3 text-danger" style={{ fontSize: '2.5rem' }}></i>
            <div>
              <h1 className="mb-0">Hire Stock</h1>
              <p className="text-muted mb-0">Manage individual rental equipment items and assets</p>
            </div>
          </div>
        </Col>
        <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
          <Button variant="primary" onClick={handleAddHireStock} className="me-2">
            Add Hire Stock
          </Button>
          <Button variant="primary" onClick={handleApplyRateClick} className="me-2">
            Apply Rate
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAllHireStock}
            disabled={hireStock.length === 0}
          >
            Delete All
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {copyProgress && (
        <Row className="mb-3">
          <Col>
            <OperationProgress
              title={copyProgress.title}
              processed={copyProgress.processed}
              total={copyProgress.total}
              success={copyProgress.success}
              failed={copyProgress.failed}
              variant="info"
            />
          </Col>
        </Row>
      )}

      <Row>
        {/* Category Filter Sidebar */}
        {hireCategories.length > 0 && (
          <Col md={2} className="pe-0">
            <Card className="h-100">
              <Card.Header className="py-2">
                <small className="fw-bold">Categories</small>
              </Card.Header>
              <div
                className="list-group list-group-flush thin-scrollbar"
                style={{
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#ccc transparent',
                }}
              >
                <button
                  type="button"
                  className={`list-group-item list-group-item-action py-2 ${selectedCategory === '' ? 'active' : ''}`}
                  onClick={() => handleCategoryFilter('')}
                >
                  <small>All Categories</small>
                </button>
                {hireCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={`list-group-item list-group-item-action py-2 ${selectedCategory === category.code ? 'active' : ''}`}
                    onClick={() => handleCategoryFilter(category.code)}
                  >
                    <small>{category.name}</small>
                  </button>
                ))}
              </div>
            </Card>
          </Col>
        )}

        <Col md={hireCategories.length > 0 ? 10 : 12}>
          <Card>
            <Card.Body>
              <Form onSubmit={handleSearch} className="mb-3">
                <Row>
                  <Col>
                    <Form.Control
                      type="text"
                      placeholder="Search hire stock..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Col>
                  <Col xs="auto">
                    <Button variant="primary" type="submit">
                      Search
                    </Button>
                  </Col>
                </Row>
              </Form>

              {loading ? (
                <div className="text-center p-3">Loading hire stock...</div>
              ) : hireStock.length === 0 ? (
                <div className="text-center p-3">No hire stock items found.</div>
              ) : (
                <>
                  <div className="table-responsive" style={{ marginLeft: 0, marginRight: 0 }}>
                    <Table
                      striped
                      bordered
                      hover
                      size="sm"
                      className="mb-3 compact-table"
                      style={{ marginLeft: 0, fontSize: '0.85rem' }}
                    >
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Stock No</th>
                          <th>Name</th>
                          <th>Day Rate</th>
                          <th>1 Day</th>
                          <th>Week Rate</th>
                          <th>Depot</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hireStock.map((item) => (
                          <tr key={item.id}>
                            <td>{getCategoryName(item.cat)}</td>
                            <td>{item.stock_no}</td>
                            <td>{item.name}</td>
                            {[
                              { key: 'day_rate', label: 'Day Rate' },
                              { key: 'one_day_rate', label: '1 Day' },
                              { key: 'week_rate', label: 'Week' },
                            ].map(({ key }) => {
                              const isEditing =
                                editingRate?.itemId === item.id && editingRate?.field === key;
                              const val = item[key];
                              const displayVal =
                                val != null && val !== '' ? `£${Number(val).toFixed(2)}` : '-';
                              return (
                                <td
                                  key={key}
                                  onClick={() =>
                                    !isEditing && setEditingRate({ itemId: item.id, field: key })
                                  }
                                  style={{ cursor: 'pointer', minWidth: '55px' }}
                                >
                                  {isEditing ? (
                                    <Form.Control
                                      type="number"
                                      size="sm"
                                      autoFocus
                                      value={val ?? ''}
                                      onChange={(e) => {
                                        const raw =
                                          e.target.value === '' ? null : parseFloat(e.target.value);
                                        const v =
                                          raw != null && !isNaN(raw)
                                            ? raw
                                            : e.target.value === ''
                                              ? null
                                              : item[key];
                                        setHireStock((prev) =>
                                          prev.map((i) =>
                                            i.id === item.id ? { ...i, [key]: v } : i
                                          )
                                        );
                                      }}
                                      onBlur={async (e) => {
                                        const raw =
                                          e.target.value === '' ? null : parseFloat(e.target.value);
                                        const v = raw != null && !isNaN(raw) ? raw : null;
                                        setHireStock((prev) =>
                                          prev.map((i) =>
                                            i.id === item.id ? { ...i, [key]: v } : i
                                          )
                                        );
                                        try {
                                          await axios.put(`/api/hire-stock/${item.id}`, {
                                            [key]: v,
                                          });
                                        } catch (err) {
                                          console.error(`Error updating ${key}:`, err);
                                          setError(`Failed to update ${key}`);
                                        }
                                        setEditingRate(null);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.target.blur();
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      step="0.01"
                                      min="0"
                                      placeholder="£"
                                      style={{ width: '70px', padding: '2px 4px' }}
                                    />
                                  ) : (
                                    <span>{displayVal}</span>
                                  )}
                                </td>
                              );
                            })}
                            <td>
                              <Form.Select
                                size="sm"
                                value={item.depot || ''}
                                onChange={(e) => handleDepotChange(item.id, e.target.value)}
                                style={{
                                  minWidth: '100px',
                                  padding: '2px 4px',
                                  fontSize: '0.8rem',
                                }}
                              >
                                <option value="">-</option>
                                <MissingSelectOption
                                  value={item.depot}
                                  options={depots}
                                  getOptionValue={(depot) => depot.short_code}
                                />
                                {depots.map((depot) => (
                                  <option key={depot.id} value={depot.short_code}>
                                    {depot.depot_name}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditHireStock(item)}
                                  style={{ padding: '1px 6px', fontSize: '0.75rem' }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => handleCopyClick(item)}
                                  disabled={copyingId === item.id}
                                  style={{ padding: '1px 6px', fontSize: '0.75rem' }}
                                >
                                  {copyingId === item.id ? '...' : 'Copy'}
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteHireStock(item.id)}
                                  style={{ padding: '1px 6px', fontSize: '0.75rem' }}
                                >
                                  Del
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <GridPagination
                    page={pagination.page}
                    pages={pagination.pages}
                    onPageChange={handlePageChange}
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showModal && (
        <HireStockModal
          key={editingHireStockId ?? 'new'}
          hireStockId={editingHireStockId}
          onClose={() => {
            setShowModal(false);
            setEditingHireStockId(null);
            setEditingHireStockItem(null);
          }}
          onSave={handleSaveHireStock}
          defaultCategory={selectedCategory}
        />
      )}

      <ApplyRateModal
        show={showApplyRateModal}
        onClose={() => setShowApplyRateModal(false)}
        items={applyRateItems}
        hireCategories={hireCategories}
        onApplied={(count) => {
          setSuccess(`Rates applied to ${count} item(s) successfully`);
          fetchHireStock(pagination.page, searchTerm, selectedCategory);
        }}
        onRefetchItems={async (categoryCode) => {
          const res = await axios.get('/api/hire-stock', {
            params: { page: 1, limit: 10000, category: categoryCode },
          });
          setApplyRateItems(res.data.hireStock || []);
        }}
        apiPath="/api/hire-stock"
        categoryName={selectedCategory ? getCategoryName(selectedCategory) : null}
      />

      <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Copy hire stock item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {copyItem && (
            <p className="mb-3">
              How many copies of <strong>{copyItem.stock_no}</strong> ({copyItem.name}) would you
              like to create?
            </p>
          )}
          <Form.Group>
            <Form.Label>Number of copies</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={100}
              value={copyCount}
              onChange={(e) =>
                setCopyCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCopyModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => copyItem && handleCopyHireStock(copyItem, copyCount)}
          >
            Create {copyCount} {copyCount === 1 ? 'copy' : 'copies'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default HireStock;
